import { Row } from 'src/shared/components/Semantic/all'
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from 'src/shared/components/ui/pagination'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/shared/components/ui/select'
import { Ellipsis } from 'lucide-react'
import { useLocalization } from 'src/shared/hooks/useLocalization'

interface PaginationComponentProps {
  activeResult: {
    count: number
    totalPages: number
  } | null
  page: number
  pageSize: number
  currentTotalPages: number
  onPageChange: (newPage: number) => void
  onPageSizeChange: (newPageSize: number) => void
}

export const PaginationComponent = ({
  activeResult,
  page,
  pageSize,
  currentTotalPages,
  onPageChange,
  onPageSizeChange,
}: PaginationComponentProps) => {
  const { t } = useLocalization()
  const isMobile = window.innerWidth < 640

  if (!activeResult || !activeResult.count || !currentTotalPages) {
    return null
  }

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages: (number | 'ellipsis')[] = []
    const total = currentTotalPages

    if (total <= 7) {
      // Show all pages if 7 or fewer
      for (let i = 1; i <= total; i++) {
        pages.push(i)
      }
    } else {
      // Always show first page
      pages.push(1)

      if (page <= 4) {
        // Near the start: 1, 2, 3, 4, 5, ..., last
        for (let i = 2; i <= 5; i++) {
          pages.push(i)
        }
        pages.push('ellipsis')
        pages.push(total)
      } else if (page >= total - 3) {
        // Near the end: 1, ..., last-4, last-3, last-2, last-1, last
        pages.push('ellipsis')
        for (let i = total - 4; i <= total; i++) {
          pages.push(i)
        }
      } else {
        // In the middle: 1, ..., page-1, page, page+1, ..., last
        pages.push('ellipsis')
        pages.push(page - 1)
        pages.push(page)
        pages.push(page + 1)
        pages.push('ellipsis')
        pages.push(total)
      }
    }

    return pages
  }

  const pageNumbers = getPageNumbers()

  return (
    <Row className="items-center w-full gap-4 flex-wrap justify-center border-t border-[hsl(var(--border-subtle))] mt-6 pt-4">
      <Pagination>
        <PaginationContent>
          <PaginationItem>
            <PaginationPrevious
              href="#"
              onClick={(e) => {
                e.preventDefault()
                if (page > 1) {
                  const newPage = page - 1
                  onPageChange(newPage)
                }
              }}
              className={page === 1 ? 'pointer-events-none opacity-50' : ''}
            />
          </PaginationItem>

          {pageNumbers.map((pageNum, index) => {
            if (pageNum === 'ellipsis') {
              return (
                <PaginationItem key={`ellipsis-${index}`}>
                  <Ellipsis className="size-4" />
                </PaginationItem>
              )
            }

            return (
              <PaginationItem key={pageNum}>
                <PaginationLink
                  href="#"
                  onClick={(e) => {
                    e.preventDefault()
                    onPageChange(pageNum)
                  }}
                  isActive={page === pageNum}
                >
                  {pageNum}
                </PaginationLink>
              </PaginationItem>
            )
          })}

          <PaginationItem>
            <PaginationNext
              href="#"
              onClick={(e) => {
                e.preventDefault()
                if (page < currentTotalPages) {
                  const newPage = page + 1
                  onPageChange(newPage)
                }
              }}
              className={
                page >= currentTotalPages
                  ? 'pointer-events-none opacity-50'
                  : ''
              }
            />
          </PaginationItem>

          {!isMobile && (
            <Row className="items-center gap-2 ml-2">
              <p className="text-sm text-muted-foreground">
                {t('Rows per page', 'Rader per sida')}
              </p>

              <Select
                value={`${pageSize}`}
                onValueChange={(value) => {
                  const newPageSize = Number(value)
                  onPageSizeChange(newPageSize)
                }}
              >
                <SelectTrigger size="small" className="w-[70px] rounded-full">
                  <SelectValue placeholder={pageSize} />
                </SelectTrigger>

                <SelectContent>
                  {[10, 25, 50, 100].map((size) => (
                    <SelectItem key={size} value={`${size}`}>
                      {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Row>
          )}
        </PaginationContent>
      </Pagination>
    </Row>
  )
}
